import { useEffect, useRef, useState } from 'react';
import TagManager from 'react-gtm-module';
import { connect, ConnectedProps } from 'react-redux';
import { Action } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';

import lang from '../../lib/language';
import { PlanPhaseTypeEnum } from '../../lib/models/crm/types';
import { RootState } from '../../lib/store';
import { getCompletedPhase } from '../../lib/store/account-flow/selectors';
import { uploadUserPreferencesData } from '../../lib/store/app';
import { isUserPreferencesResolved } from '../../lib/store/app/helpers';
import { AppCall } from '../../lib/store/app/types';
import { getCurrentAccountTradingPlan, normalisePlanPhaseKey } from '../../lib/store/crm/helpers';
import { isCallStatusPending } from '../../lib/util/error-handling/StatusByCallHelpers';

import bigThumbsUp from '../../assets/bigImages/BigThumbsUp.png';
import hurrah from '../../assets/bigImages/hurrah.png';
import { GTM_CATEGORIES, GTM_CHALLENGE_PROGRESS, GTM_EVENTS } from '../../constants/gtm';
import { useIsMobile } from '../../hooks';
import BaseModal from '../../shared_components/BaseModal/BaseModal';
import Button from '../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../shared_components/Button/Button.types';
import { MobileDrawer } from '../index';

import styles from './PlanTargetModal.module.scss';

const PlanTargetModal = ({
  completedPhase,
  tradingPlan,
  uploadPreferences,
  userPreferencesData,
  isUserPreferencesPending,
  arePreferencesResolved,
  userId,
  isUseDemoAccount,
}: ReduxProps) => {
  const { isMobile } = useIsMobile();
  const [showModal, setShowModal] = useState<boolean>(false);
  const hasTrackedPhaseCompleted = useRef<boolean>(false);
  const hasAcknowledged = useRef<boolean>(false);
  /** Once the modal has been shown it renders until the user acts - see the guard below. */
  const hasOpened = useRef<boolean>(false);
  const openedForPhase = useRef<string>('');
  const normalisePhase = completedPhase ? normalisePlanPhaseKey(completedPhase) : '';
  const isChallengePhase = completedPhase === PlanPhaseTypeEnum.challenge;
  const isModalShowed: boolean = userPreferencesData ? userPreferencesData[`${normalisePhase}TargetModalShowed`] : false;

  const trackPhaseCompleted = () => {
    if (hasTrackedPhaseCompleted.current) return;
    hasTrackedPhaseCompleted.current = true;
    TagManager.dataLayer({
      dataLayer: {
        event: GTM_EVENTS.CHALLENGE_PROGRESS_UPDATE,
        event_category: GTM_CATEGORIES.CHALLENGE_MANAGEMENT,
        event_action: GTM_CHALLENGE_PROGRESS.ACTION,
        event_label: tradingPlan?.phase,
        client_id: userId,
        challenge_id: tradingPlan?.reference_id,
        challenge_name: tradingPlan?.name,
        buying_power: tradingPlan?.daily_hard_bp,
      },
    });
  };

  const acknowledge = () => {
    if (hasAcknowledged.current) return;
    hasAcknowledged.current = true;
    setShowModal(false);
    uploadPreferences({
      ...userPreferencesData,
      [`${openedForPhase.current}TargetModalShowed`]: true,
    });
  };

  const close = () => setShowModal(false);

  useEffect(() => {
    if (!arePreferencesResolved
      || isUserPreferencesPending
      || !normalisePhase
      || showModal
      || hasAcknowledged.current
      || isUseDemoAccount
    ) {
      return;
    }

    if (!isModalShowed) {
      openedForPhase.current = normalisePhase;
      hasOpened.current = true;
      setShowModal(true);
      trackPhaseCompleted();
    }
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    arePreferencesResolved,
    isUserPreferencesPending,
    isModalShowed,
    normalisePhase,
    showModal,
  ]);

  if (!hasOpened.current && (!normalisePhase || isUseDemoAccount)) {
    return null;
  }

  const renderContent = () => (
    <>
      <div className={styles.iconHolder} id="planTargetModal">
        <img src={isChallengePhase ? bigThumbsUp : hurrah} alt="Thumb icon" />
      </div>
      <div className={styles.textWrapper}>
        <div className={styles.title}>{isChallengePhase ? lang.challengePassed() : lang.nextStageReached()}</div>
        <div className={styles.text}>
          <p>{isChallengePhase ? lang.challengePassedDescription() : lang.nextStageReachedDescription()}</p>
        </div>
      </div>
      <div className={styles.buttonsWrapper}>
        <Button biggerFontSize fullWidth variant={ButtonTypesEnum.Primary} onClick={acknowledge}>
          {lang.commonOkay()}
        </Button>
      </div>
    </>
  );

  return (
    <>
      {isMobile ? (
        <MobileDrawer
          isOpen={showModal}
          title=""
          onClose={close}
          showCloseButton={false}
        >
          {renderContent()}
        </MobileDrawer>
      ) : (
        <BaseModal isOpen={showModal} toggle={close}>
          {renderContent()}
        </BaseModal>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  completedPhase: getCompletedPhase(state),
  tradingPlan: getCurrentAccountTradingPlan(state.crm),
  userPreferencesData: state.app.userPreferencesData,
  arePreferencesResolved: isUserPreferencesResolved(state.app),
  isUserPreferencesPending:
    isCallStatusPending(AppCall.uploadUserPreferencesData, state.app.statusByCall)
    || isCallStatusPending(AppCall.getUserPreferencesData, state.app.statusByCall),
  userId: state.auth.enroll.userId,
  isUseDemoAccount: state.crm.isUseDemoAccount,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  uploadPreferences: (payload: any) => dispatch(uploadUserPreferencesData(payload)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(PlanTargetModal);
